import { Component, inject, OnInit, signal } from '@angular/core';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { BrandService } from '../../../services/brand.service';
import { Brand, CreateBrand, UpdateBrand } from '../../../models/brand.model';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-brand-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ProgressSpinnerModule,
    ToggleSwitchModule,
    ToolbarModule,
    ToastModule,
  ],
  templateUrl: './brand-create-or-update.component.html',
})
export class BrandCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private brandService = inject(BrandService);
  private formGroupService = inject(FormGroupService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  private readonly maxImageSizeBytes = 5 * 1024 * 1024; // 5MB

  loading = signal(false);

  brandId = signal<string | null>(null);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(255)]],
    image: [null],
    logoFileId: [null],
    logoUrl: [null],
    isActive: [true],
  });

  selectedLogoPreviewUrl = signal<string | null>(null);
  private selectedLogoObjectUrl: string | null = null;

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.brandId.set(id);
        this.loadData(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokeSelectedLogoObjectUrl();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (!file.type!.startsWith('image/')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `O arquivo deve ser uma imagem (ex: jpg, png, webp, etc.).`,
        life: 5000,
      });
      return;
    }

    if (file.size > this.maxImageSizeBytes) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `A image deve ter no máximo 5MB.`,
        life: 5000,
      });
      return;
    }

    if (
      this.formGroup.get('logoUrl')?.value ||
      this.formGroup.get('logoFileId')?.value
    ) {
      this.formGroup.patchValue({
        logoUrl: null,
        logoFileId: null,
      });
    }

    this.revokeSelectedLogoObjectUrl();
    this.selectedLogoObjectUrl = URL.createObjectURL(file);
    this.selectedLogoPreviewUrl.set(this.selectedLogoObjectUrl);

    this.formGroup.patchValue({ image: file });
    this.formGroup.get('image')!.updateValueAndValidity();
  }

  removeLogo(): void {
    this.formGroup.patchValue({
      image: null,
      logoFileId: null,
      logoUrl: null,
    });

    this.formGroup.get('image')?.updateValueAndValidity();

    this.revokeSelectedLogoObjectUrl();
    this.selectedLogoPreviewUrl.set(null);
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }
    if (this.formGroup.value.id) {
      this.update(this.formGroup.getRawValue() as UpdateBrand);
    } else {
      let data = this.formGroup.getRawValue();
      delete data.id;
      this.create(data as CreateBrand);
    }
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.brandService.getById(id).subscribe({
      next: (response: ApiResult) => {
        const brand = response.data as Brand;
        this.formGroup.patchValue(brand);
        this.loading.set(false);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a marca',
            life: 3000,
          });
        }
      },
    });
  }

  private create(data: CreateBrand): void {
    this.loading.set(true);

    const formValue = this.formGroup.value;

    const request = new FormData();
    request.append('name', data.name);
    request.append('description', data.description ?? '');
    if (formValue.image) {
      request.append('image', formValue.image);
    }

    this.brandService.create(request).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Marca cadastrada com sucesso!',
          life: 2000,
        });

        this.loading.set(false);
        this.router.navigate(['/brands']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Fail',
              detail: 'Ocorreu um erro ao cadastrar a marca',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private update(data: UpdateBrand): void {
    this.loading.set(true);

    const formValue = this.formGroup.value;

    const request = new FormData();
    request.append('id', data.id);
    request.append('name', data.name);
    request.append('description', data.description ?? '');
    if (data.logoFileId) request.append('logoFileId', data.logoFileId);
    request.append('isActive', data.isActive.toString());
    if (formValue.image) request.append('image', formValue.image);

    this.brandService.update(data.id, request).subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Marca atualizada com sucesso!',
          life: 2000,
        });

        this.router.navigate(['/brands']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Fail',
              detail: 'Ocorreu um erro ao atualizar a marca',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private revokeSelectedLogoObjectUrl(): void {
    if (this.selectedLogoObjectUrl) {
      URL.revokeObjectURL(this.selectedLogoObjectUrl);
      this.selectedLogoObjectUrl = null;
    }
  }
}
